/**
 * DateRangePicker — **ช่วงวันที่คือค่าเดียว ไม่ใช่สองช่องที่บังเอิญอยู่ติดกัน** (D-120)
 *
 * ผู้ใช้สั่ง 2026-09-25 ว่า "จาก / ถึง ให้เลือกได้ใน Calendar เดียวกัน" — ผลที่ตามมา
 * ไม่ใช่แค่เรื่องความสะดวก: ปฏิทินเดียวที่คลิกแรกเป็นวันเริ่มและคลิกที่สองเป็นวัน
 * สิ้นสุด **ทำให้ช่วงกลับหัวเกิดจากหน้าจอไม่ได้เลย** (คลิกที่สองก่อนวันเริ่มถูกสลับ
 * ให้เอง) ต่างจากสองช่อง `type=date` ที่กรอกสลับกันได้ตลอดเวลา
 *
 * **component นี้ไม่รู้จักโมดูลใดเลย** (D-120 ข้อ 1) — Comment 4 ให้ทุกโมดูลมีตัวกรอง
 * เหมือนกัน และหน้าอื่นที่มีช่วงวันที่ (Executive Dashboard ฯลฯ) จะย้ายมาใช้ตัวนี้
 * ทีหลัง สิ่งที่มันรับคือ ISO `YYYY-MM-DD` สองตัว (หรือสตริงว่าง = ไม่ได้กรอง) และ
 * สิ่งที่มันคืนคือสองตัวเดียวกัน — **ไม่มี `Date` object ใดข้ามเส้น props เลย**
 *
 * ⚠️ **ค่าวันต้องคำนวณแบบวันปฏิทินล้วน ห้ามผ่าน `Date.prototype.toISOString()`**
 * `toISOString()` แปลงเป็น UTC ก่อนเสมอ เครื่องที่ตั้งเวลาไว้ `Asia/Bangkok` (UTC+7,
 * D-055) จึงได้ **วันก่อนหน้าหนึ่งวัน** ทุกครั้งที่เลือกวันที่ 1 — วันที่เลื่อนไปหนึ่งวัน
 * คือตัวกรองที่ตัดใบของวันแรกของเดือนทิ้งทุกเดือนโดยไม่มีใครเห็น การคำนวณทั้งหมดที่นี่
 * จึงเป็นเลขคณิตของ "ปี/เดือน/วัน" ล้วน ๆ (ดู `isoDate`, `addDays`, `addMonths`)
 *
 * **ห้ามเพิ่ม dependency** — แอปไม่มีไลบรารีวันที่เลย และปฏิทินหนึ่งเดือนเขียนเองได้
 * ในไฟล์เดียว (D-120 ข้อ 1)
 *
 * ไม่ใช่ `Modal`: popover นี้ **ไม่ดักโฟกัส** ผู้ใช้ Tab ออกไปยังส่วนที่เหลือของหน้าได้
 * ตามปกติ — แนวเดียวกับ `OptionsMenu` ส่วนที่ต่างคือมันวางโฟกัสไว้ที่ "วันที่กำลังเล็ง"
 * ตอนเปิด เพื่อให้ลูกศรใช้งานได้ทันทีโดยไม่ต้อง Tab เข้าไปหาเอง (D-120 ข้อ 4)
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import './date-range-picker.css';

export interface DateRangePickerProps {
  /** ชื่อของตัวกรองนี้ เช่น "วันที่บันทึก" — เป็นทั้ง label และชื่อของ popover */
  readonly label: string;
  /** ปลาย "จาก" เป็น ISO `YYYY-MM-DD` หรือ `''` เมื่อไม่ได้กรอง */
  readonly from: string;
  /** ปลาย "ถึง" เป็น ISO `YYYY-MM-DD` หรือ `''` เมื่อไม่ได้กรอง */
  readonly to: string;
  /** เรียก **ครั้งเดียวต่อการเลือกหนึ่งช่วง** — ไม่ใช่ครั้งละปลาย */
  readonly onChange: (from: string, to: string) => void;
  /** คำนำหน้าของ `data-testid` ทุกตัวในนี้ — ไม่ใส่ = ไม่มี `data-testid` เลย */
  readonly testId?: string;
  /** (i) ข้าง label — ที่เดียวที่คำอธิบายอยู่ได้ (D-119) */
  readonly hint?: ReactNode;
}

/** ช่องว่างเปล่าทั้งคู่ — ยังไม่ได้กรองอะไรเลย */
const PLACEHOLDER = 'เลือกช่วงวันที่';
/** ปลายที่ยังไม่มีค่า (มาจาก URL ที่ใส่มาข้างเดียวได้) */
const OPEN_END = '…';
/** ตัวคั่นช่วง — ตัวเดียวกับที่คอลัมน์ "วันส่ง" ในตารางใช้ */
const RANGE_SEPARATOR = ' – ';

const THAI_MONTHS: readonly string[] = [
  'มกราคม',
  'กุมภาพันธ์',
  'มีนาคม',
  'เมษายน',
  'พฤษภาคม',
  'มิถุนายน',
  'กรกฎาคม',
  'สิงหาคม',
  'กันยายน',
  'ตุลาคม',
  'พฤศจิกายน',
  'ธันวาคม',
];

/** หัวคอลัมน์ของตารางวัน — `aria-hidden` เพราะแต่ละวันประกาศวันที่เต็มของตัวเองอยู่แล้ว */
const THAI_WEEKDAYS: readonly string[] = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

interface CalendarMonth {
  readonly year: number;
  /** 0-11 แบบเดียวกับ `Date.getMonth()` */
  readonly month: number;
}

interface CalendarDay extends CalendarMonth {
  readonly day: number;
}

function pad(value: number, width: number): string {
  return String(Math.abs(value)).padStart(width, '0');
}

/**
 * ISO ของ **วันปฏิทิน** — ประกอบจากตัวเลขตรง ๆ ไม่แตะ `Date` เลย
 *
 * นี่คือจุดเดียวที่วันกลายเป็นสตริง และเหตุผลที่มันไม่ใช้ `toISOString()` อยู่หัวไฟล์
 */
function isoDate({ year, month, day }: CalendarDay): string {
  return `${pad(year, 4)}-${pad(month + 1, 2)}-${pad(day, 2)}`;
}

/**
 * เลขคณิตปฏิทินทำบน **UTC ล้วน** โดยตั้งใจ — ไม่ใช่เพราะเราสนใจ UTC แต่เพราะ UTC
 * ไม่มี DST และไม่มี offset ของเครื่องมาเกี่ยว "บวกหนึ่งวัน" จึงแปลว่าบวกหนึ่งวันเสมอ
 *
 * ค่าที่ออกจากฟังก์ชันกลุ่มนี้เป็นตัวเลขปี/เดือน/วัน **ไม่ใช่ instant** — ไม่มีใครนำ
 * `Date` ตัวนี้ไป format ต่อ
 */
function probeOf(year: number, month: number, day: number): Date {
  const probe = new Date(Date.UTC(2000, 0, 1));
  probe.setUTCFullYear(year, month, day);
  return probe;
}

function daysInMonth({ year, month }: CalendarMonth): number {
  // วันที่ 0 ของเดือนถัดไป = วันสุดท้ายของเดือนนี้
  return probeOf(year, month + 1, 0).getUTCDate();
}

function firstWeekdayOfMonth({ year, month }: CalendarMonth): number {
  return probeOf(year, month, 1).getUTCDay();
}

function addDays(day: CalendarDay, delta: number): CalendarDay {
  const probe = probeOf(day.year, day.month, day.day + delta);
  return {
    year: probe.getUTCFullYear(),
    month: probe.getUTCMonth(),
    day: probe.getUTCDate(),
  };
}

function addMonths(month: CalendarMonth, delta: number): CalendarMonth {
  const probe = probeOf(month.year, month.month + delta, 1);
  return { year: probe.getUTCFullYear(), month: probe.getUTCMonth() };
}

/**
 * วันนี้ตามปฏิทิน **ของเครื่องผู้ใช้** — อ่านทีละส่วนด้วย getter ท้องถิ่น
 *
 * `new Date()` เป็น instant จริง ๆ อันเดียวในไฟล์นี้ และมันถูกแปลงเป็นปี/เดือน/วัน
 * ทันทีตรงนี้จุดเดียว
 */
function today(): CalendarDay {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() };
}

function parseIsoDate(value: string): CalendarDay | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (match === null) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (month < 0 || month > 11 || day < 1 || day > daysInMonth({ year, month })) {
    return null;
  }
  return { year, month, day };
}

/**
 * ข้อความบนช่องเดียว (D-120 ข้อ 2) — ปลายที่ขาดไปแสดงเป็น `…` ไม่ใช่หายไปเฉย ๆ
 *
 * ปลายข้างเดียวมาจาก URL ที่พิมพ์เองได้ (เซิร์ฟเวอร์รับ) ปฏิทินสร้างมันไม่ได้ก็จริง
 * แต่หน้าจอต้อง **แสดงมันถูก** ไม่งั้นลิงก์ที่ส่งต่อกันจะอ่านเหมือนไม่ได้กรองอะไรเลย
 */
function rangeLabel(from: string, to: string): string {
  const start = from.trim();
  const end = to.trim();
  if (start === '' && end === '') {
    return PLACEHOLDER;
  }
  return `${start === '' ? OPEN_END : start}${RANGE_SEPARATOR}${end === '' ? OPEN_END : end}`;
}

function monthLabel({ year, month }: CalendarMonth): string {
  return `${THAI_MONTHS[month]} ${pad(year, 4)}`;
}

/** ชื่อที่ screen reader อ่านให้ปุ่มวัน — วันที่เต็ม ไม่ใช่เลขวันลอย ๆ (D-120 ข้อ 4) */
function dayLabel({ year, month, day }: CalendarDay): string {
  return `${day} ${THAI_MONTHS[month]} ${pad(year, 4)}`;
}

/** เรียงสองปลายให้ถูกทาง — เทียบสตริง ISO ได้ตรง ๆ เพราะมันเรียงตามเวลาอยู่แล้ว */
function orderedRange(a: string, b: string): readonly [string, string] {
  return a <= b ? [a, b] : [b, a];
}

export function DateRangePicker({
  label,
  from,
  to,
  onChange,
  testId,
  hint,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  /** เดือนที่ปฏิทินกำลังแสดง — ตั้งใหม่ทุกครั้งที่เปิด ไม่ใช่ค้างจากครั้งก่อน */
  const [month, setMonth] = useState<CalendarMonth>(() => parseIsoDate(from) ?? today());
  /** คลิกแรกที่ยังไม่มีคลิกที่สอง — `null` คือ "ยังไม่ได้เริ่มเลือก" */
  const [draftStart, setDraftStart] = useState<string | null>(null);
  /** วันที่เมาส์อยู่ระหว่างเลือก — ใช้ไฮไลต์ช่วงตามเมาส์ (D-120 ข้อ 2) */
  const [hovered, setHovered] = useState<string | null>(null);
  /** วันที่ถือ `tabIndex=0` ในตาราง — ลูกศรเลื่อนตัวนี้ ไม่ใช่ Tab ทีละวัน */
  const [focusedDay, setFocusedDay] = useState<string>(() =>
    isoDate(parseIsoDate(from) ?? today()),
  );

  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const focusedDayRef = useRef<HTMLButtonElement | null>(null);
  /**
   * โฟกัสย้ายไปที่วันเฉพาะตอนที่ **คีย์บอร์ดเป็นคนสั่ง** (เปิด, ลูกศร, PageUp/Down)
   *
   * ปุ่มเดือนก่อน/ถัดไปเปลี่ยนเดือนโดยไม่แย่งโฟกัสไปจากตัวมันเอง — ไม่งั้นการกด
   * "เดือนถัดไป" สองครั้งติดกันจะทำไม่ได้เลยด้วยคีย์บอร์ด
   */
  const shouldFocusDayRef = useRef(false);

  const labelId = useId();
  const triggerId = useId();
  const popoverId = useId();

  const testIdFor = (suffix: string): string | undefined =>
    testId === undefined ? undefined : `${testId}-${suffix}`;

  useEffect(() => {
    if (!open || !shouldFocusDayRef.current) {
      return;
    }
    shouldFocusDayRef.current = false;
    focusedDayRef.current?.focus();
  }, [open, focusedDay, month]);

  const closeWithoutChanging = (returnFocus: boolean) => {
    setOpen(false);
    setDraftStart(null);
    setHovered(null);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        // คลิกนอกกล่อง = ปิดโดย **ไม่เปลี่ยนค่า** (D-120 ข้อ 2) และไม่ดึงโฟกัสกลับมา
        // แย่งจากสิ่งที่ผู้ใช้เพิ่งคลิก
        setOpen(false);
        setDraftStart(null);
        setHovered(null);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      // กันไม่ให้ Esc ทะลุไปสั่งอย่างอื่นบนหน้าจอที่อยู่หลัง popover
      event.stopPropagation();
      setOpen(false);
      setDraftStart(null);
      setHovered(null);
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  /**
   * เปิดปฏิทิน — **ที่เดือนของ "จาก" หรือเดือนปัจจุบัน** (D-120 ข้อ 2)
   *
   * ตั้งค่าใหม่ทุกครั้ง: การเลือกที่ค้างครึ่งทางจากการเปิดครั้งก่อนคือช่วงที่ผู้ใช้
   * ไม่ได้ตั้งใจให้เกิด
   */
  const openPicker = () => {
    const anchor = parseIsoDate(from) ?? today();
    setMonth({ year: anchor.year, month: anchor.month });
    setFocusedDay(isoDate(anchor));
    setDraftStart(null);
    setHovered(null);
    shouldFocusDayRef.current = true;
    setOpen(true);
  };

  /** เปลี่ยนเดือนที่แสดง และลากวันที่ถือโฟกัสตามไปด้วย (ตัดให้ไม่เกินสิ้นเดือน) */
  const goToMonth = (next: CalendarMonth) => {
    const current = parseIsoDate(focusedDay);
    setMonth(next);
    setFocusedDay(
      isoDate({ ...next, day: Math.min(current?.day ?? 1, daysInMonth(next)) }),
    );
  };

  const moveFocusByDays = (delta: number) => {
    const current = parseIsoDate(focusedDay) ?? today();
    const next = addDays(current, delta);
    setMonth({ year: next.year, month: next.month });
    setFocusedDay(isoDate(next));
    shouldFocusDayRef.current = true;
  };

  const moveFocusByMonths = (delta: number) => {
    goToMonth(addMonths(month, delta));
    shouldFocusDayRef.current = true;
  };

  /**
   * คลิกแรก = วันเริ่ม, คลิกที่สอง = วันสิ้นสุด แล้ว **เรียก `onChange` ครั้งเดียว**
   *
   * คลิกที่สองก่อนวันเริ่มถูกสลับให้เอง (`orderedRange`) — **นี่คือเหตุผลทั้งหมดที่
   * D-120 มีอยู่**: ช่วงกลับหัวเกิดจากปฏิทินไม่ได้ คลิกวันเดิมสองครั้ง = ช่วงวันเดียว
   */
  const selectDay = (iso: string) => {
    if (draftStart === null) {
      setDraftStart(iso);
      setHovered(iso);
      setFocusedDay(iso);
      return;
    }
    const [start, end] = orderedRange(draftStart, iso);
    setOpen(false);
    setDraftStart(null);
    setHovered(null);
    onChange(start, end);
    triggerRef.current?.focus();
  };

  const clearRange = () => {
    setOpen(false);
    setDraftStart(null);
    setHovered(null);
    onChange('', '');
    triggerRef.current?.focus();
  };

  /**
   * วันนี้อยู่ในช่วงที่ควรไฮไลต์ไหม — ระหว่างเลือกคือช่วง "คลิกแรก → เมาส์"
   * หลังเลือกเสร็จคือช่วงที่ถืออยู่จริง (ปลายข้างเดียวไฮไลต์แค่วันนั้นวันเดียว)
   */
  const isInRange = (iso: string): boolean => {
    if (draftStart !== null) {
      const [start, end] = orderedRange(draftStart, hovered ?? draftStart);
      return iso >= start && iso <= end;
    }
    if (from !== '' && to !== '') {
      return iso >= from && iso <= to;
    }
    return iso === from || iso === to;
  };

  const isEndpoint = (iso: string): boolean =>
    draftStart !== null ? iso === draftStart : iso === from || iso === to;

  const todayIso = isoDate(today());
  const leadingBlanks = firstWeekdayOfMonth(month);
  const dayCount = daysInMonth(month);
  const days = Array.from({ length: dayCount }, (_, index) => index + 1);

  return (
    <div className="cap-field cap-date-range" ref={rootRef}>
      <span className="cap-field__label">
        <span id={labelId}>{label}</span>
        {hint === undefined ? null : <> {hint}</>}
      </span>

      <button
        type="button"
        id={triggerId}
        ref={triggerRef}
        className={`cap-input cap-date-range__trigger${
          from === '' && to === '' ? ' cap-date-range__trigger--empty' : ''
        }`}
        data-testid={testId}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-labelledby={`${labelId} ${triggerId}`}
        onClick={() => {
          if (open) {
            closeWithoutChanging(false);
            return;
          }
          openPicker();
        }}
      >
        {rangeLabel(from, to)}
      </button>

      {open ? (
        <div
          id={popoverId}
          className="cap-date-range__popover"
          role="dialog"
          aria-label={`ปฏิทินเลือกช่วงวันที่: ${label}`}
          data-testid={testIdFor('popover')}
        >
          <div className="cap-date-range__head">
            <button
              type="button"
              className="cap-button cap-button--ghost cap-date-range__nav"
              data-testid={testIdFor('prev-month')}
              aria-label="เดือนก่อนหน้า"
              onClick={() => {
                goToMonth(addMonths(month, -1));
              }}
            >
              ‹
            </button>
            <span
              className="cap-date-range__month"
              data-testid={testIdFor('month')}
              aria-live="polite"
            >
              {monthLabel(month)}
            </span>
            <button
              type="button"
              className="cap-button cap-button--ghost cap-date-range__nav"
              data-testid={testIdFor('next-month')}
              aria-label="เดือนถัดไป"
              onClick={() => {
                goToMonth(addMonths(month, 1));
              }}
            >
              ›
            </button>
          </div>

          <div className="cap-date-range__weekdays" aria-hidden="true">
            {THAI_WEEKDAYS.map((weekday, index) => (
              <span className="cap-date-range__weekday" key={index}>
                {weekday}
              </span>
            ))}
          </div>

          {/*
            ลูกศร/PageUp/PageDown ดักที่ตารางวัน ไม่ใช่ทั้ง popover — ปุ่มเดือนและปุ่ม
            "ล้าง" ยังใช้ลูกศรของเบราว์เซอร์ตามปกติ
          */}
          <div
            className="cap-date-range__grid"
            data-testid={testIdFor('grid')}
            onKeyDown={(event) => {
              const steps: Readonly<Record<string, number | undefined>> = {
                ArrowLeft: -1,
                ArrowRight: 1,
                ArrowUp: -7,
                ArrowDown: 7,
              };
              const step = steps[event.key];
              if (step !== undefined) {
                event.preventDefault();
                moveFocusByDays(step);
                return;
              }
              if (event.key === 'PageUp' || event.key === 'PageDown') {
                event.preventDefault();
                moveFocusByMonths(event.key === 'PageUp' ? -1 : 1);
              }
            }}
            onMouseLeave={() => {
              setHovered(draftStart);
            }}
          >
            {Array.from({ length: leadingBlanks }, (_, index) => (
              <span className="cap-date-range__blank" key={`blank-${index}`} />
            ))}
            {days.map((day) => {
              const iso = isoDate({ ...month, day });
              const selected = isInRange(iso);
              const roving = iso === focusedDay;
              return (
                <button
                  type="button"
                  key={iso}
                  ref={roving ? focusedDayRef : undefined}
                  className={`cap-date-range__day${
                    selected ? ' cap-date-range__day--in-range' : ''
                  }${isEndpoint(iso) ? ' cap-date-range__day--endpoint' : ''}${
                    iso === todayIso ? ' cap-date-range__day--today' : ''
                  }`}
                  data-testid={testIdFor(`day-${iso}`)}
                  data-date={iso}
                  aria-label={dayLabel({ ...month, day })}
                  aria-pressed={selected}
                  aria-current={iso === todayIso ? 'date' : undefined}
                  tabIndex={roving ? 0 : -1}
                  onMouseEnter={() => {
                    setHovered(iso);
                  }}
                  onFocus={() => {
                    setFocusedDay(iso);
                  }}
                  onClick={() => {
                    selectDay(iso);
                  }}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="cap-date-range__foot">
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid={testIdFor('clear')}
              onClick={clearRange}
            >
              ล้าง
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
